import { useState } from "react";
import { notify } from "../components/UI/DesignStandards/Notifications/notify";
import { validatePhone } from "../utils/validation";
import { sanitizePhone, sanitizeUsbcId } from "../utils/sanitization";

/**
 * Hook to manage profile field updates (name, phone, USBC ID)
 * @param {Object} params
 * @param {Object} params.profile - Current user profile
 * @param {Function} params.updateProfile - Function to update profile
 * @returns {Object} Profile field state and handlers
 */
export const useProfileFields = ({ profile, updateProfile }) => {
  const [loading, setLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [editingUsbcId, setEditingUsbcId] = useState(false);
  const [errors, setErrors] = useState({});

  /**
   * Update user's full name
   * @param {string} fullName - New full name
   */
  const handleNameUpdate = async (fullName) => {
    if (!fullName.trim()) {
      notify.error("Name cannot be empty");
      return;
    }

    setLoading(true);
    try {
      const { error } = await updateProfile({
        full_name: fullName.trim(),
      });

      if (error) throw new Error(error);

      setEditingName(false);
      notify.success("Name updated successfully");
    } catch (error) {
      notify.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update user's phone number
   * @param {string} phone - New phone number
   */
  const handlePhoneUpdate = async (phone) => {
    const phoneValidation = validatePhone(phone);
    if (!phoneValidation.isValid) {
      setErrors((prev) => ({
        ...prev,
        phone: phoneValidation.error,
      }));
      return;
    }

    setLoading(true);
    try {
      // Sanitize phone to digits only (consistent with other profile updates)
      const sanitizedPhone = phone ? sanitizePhone(phone) : null;
      const { error } = await updateProfile({
        phone_number: sanitizedPhone,
      });

      if (error) throw new Error(error);

      setEditingPhone(false);
      notify.success("Phone number updated successfully");
    } catch (error) {
      notify.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update user's USBC ID
   * @param {string} usbcId - New USBC ID
   */
  const handleUsbcIdUpdate = async (usbcId) => {
    // Validate USBC ID
    if (usbcId && usbcId.trim()) {
      const cleanedUsbcId = usbcId.replace(/\D/g, "");
      if (cleanedUsbcId.length > 15) {
        setErrors((prev) => ({
          ...prev,
          usbcId: "USBC ID must be 15 digits or less",
        }));
        return;
      }
    }

    setLoading(true);
    try {
      // Sanitize: only digits, limit to 15
      const sanitizedUsbcId = usbcId ? sanitizeUsbcId(usbcId) : null;
      const { error } = await updateProfile({
        usbcId: sanitizedUsbcId,
      });

      if (error) throw new Error(error);

      setEditingUsbcId(false);
      notify.success("USBC ID updated successfully");
    } catch (error) {
      notify.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancel editing a field
   * @param {string} field - Field name ('name', 'phone', 'usbcId')
   * @param {Function} resetFormData - Function to reset form data for the field
   */
  const cancelEdit = (field, resetFormData) => {
    if (field === "name") {
      setEditingName(false);
    } else if (field === "phone") {
      setEditingPhone(false);
    } else if (field === "usbcId") {
      setEditingUsbcId(false);
    }
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
    if (resetFormData) {
      resetFormData();
    }
  };

  return {
    // State
    loading,
    editingName,
    editingPhone,
    editingUsbcId,
    errors,
    // Actions
    setEditingName,
    setEditingPhone,
    setEditingUsbcId,
    handleNameUpdate,
    handlePhoneUpdate,
    handleUsbcIdUpdate,
    cancelEdit,
    setErrors,
  };
};

